import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { API_BASE_URL } from '../lib/utils';
import { useAppSelector } from '../store';

export interface ConfigListItem {
  id: string;
  label: string;
  enabled: boolean;
  color?: string;
  category?: string;
}

export interface RoleConfig {
  id: string;
  name: string;
  colorClass: string;
  permissions: string[];
}

export interface SystemConfig {
  eventCategories: ConfigListItem[];
  incidentCategories: ConfigListItem[];
  incidentSeverities: ConfigListItem[];
  incidentStatuses: ConfigListItem[];
  eventStatuses: ConfigListItem[];
  resourceTypes: ConfigListItem[];
  skillLevels: ConfigListItem[];
  departments: ConfigListItem[];
  permissions: ConfigListItem[];
  roles: RoleConfig[];
}

interface SystemConfigContextValue {
  config: SystemConfig | null;
  loading: boolean;
  refreshConfig: () => Promise<void>;
  updateConfig: (updates: Partial<SystemConfig>) => Promise<void>;
  // Convenience getters for enabled items only
  enabledEventCategories: ConfigListItem[];
  enabledIncidentCategories: ConfigListItem[];
  enabledIncidentSeverities: ConfigListItem[];
  enabledResourceTypes: ConfigListItem[];
  enabledDepartments: ConfigListItem[];
}

const defaultValue: SystemConfigContextValue = {
  config: null,
  loading: true,
  refreshConfig: async () => {},
  updateConfig: async () => {},
  enabledEventCategories: [],
  enabledIncidentCategories: [],
  enabledIncidentSeverities: [],
  enabledResourceTypes: [],
  enabledDepartments: [],
};

const SystemConfigContext = createContext<SystemConfigContextValue>(defaultValue);

export function SystemConfigProvider({ children }: { children: ReactNode }) {
  const { token } = useAppSelector((state) => state.auth);
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshConfig = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/system-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refreshConfig(); }, [refreshConfig]);

  const updateConfig = useCallback(async (updates: Partial<SystemConfig>) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/system-config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
      }
    } catch { /* ignore */ }
  }, [token]);

  const enabled = (list?: ConfigListItem[]) => (list || []).filter((i) => i.enabled);

  return (
    <SystemConfigContext.Provider value={{
      config,
      loading,
      refreshConfig,
      updateConfig,
      enabledEventCategories: enabled(config?.eventCategories),
      enabledIncidentCategories: enabled(config?.incidentCategories),
      enabledIncidentSeverities: enabled(config?.incidentSeverities),
      enabledResourceTypes: enabled(config?.resourceTypes),
      enabledDepartments: enabled(config?.departments),
    }}>
      {children}
    </SystemConfigContext.Provider>
  );
}

export function useSystemConfig() {
  return useContext(SystemConfigContext);
}
